import { useMemo } from "react";
import { Product } from "tm-shared/types";

import { useAppSelector } from "@/hooks/redux";

type FilterValue = string | number | boolean;
type FiltersMap = Record<string, FilterValue[]>;

type ParamKey = keyof Product["params"];

const getParam = (product: Product, tag: string) =>
    product.params[tag as ParamKey];

export const useFilters = <TProduct extends Product>({
    products,
    filters,
    inventoryList = {},
    hideZeroProducts = false,
    showOnlyMML = false,
    mmlList,
}: {
    products: Record<string, TProduct>;
    inventoryList?: Record<string, number>;
    filters: FiltersMap;
    hideZeroProducts?: boolean;
    showOnlyMML?: boolean;
    mmlList?: Record<string, boolean>;
}) => {
    const filteredParams = useAppSelector((s) => s.offline.filteredParams);

    const filteredProducts = useMemo(() => {
        return Object.values(products).filter((product) => {
            const { barcode } = product;

            if (hideZeroProducts && (inventoryList[barcode] ?? 0) === 0)
                return false;

            if (mmlList && showOnlyMML) return !!mmlList?.[barcode];

            return Object.entries(filters).every(([tag, values]) => {
                if (!values?.length) return true;

                const param = getParam(product, tag);
                if (!param) return false;

                const productValue = param.value;

                return Array.isArray(productValue)
                    ? productValue.some((v) => values.includes(v))
                    : values.includes(productValue);
            });
        });
    }, [
        products,
        hideZeroProducts,
        inventoryList,
        showOnlyMML,
        filters,
        mmlList,
    ]);

    const initialFacets = useMemo(() => {
        return filteredParams.filter((tag) =>
            Object.values(products).every((p) => tag in p.params),
        );
    }, [filteredParams, products]);

    const availableFilters = useMemo(() => {
        const filteredArray = filteredProducts;
        const firstProduct = filteredArray[0];

        return initialFacets.reduce(
            (acc, tag) => {
                const values = new Set<FilterValue>();

                filteredArray.forEach((product) => {
                    const param = getParam(product, tag);
                    if (!param) return;

                    if (Array.isArray(param.value)) {
                        param.value.forEach((v) => values.add(v));
                    } else {
                        values.add(param.value);
                    }
                });

                if (values.size > 0) {
                    acc[tag] = {
                        title: firstProduct
                            ? getParam(firstProduct, tag)?.paramName ?? tag
                            : tag,
                        options: Array.from(values).sort((a, b) => {
                            if (typeof a === "number" && typeof b === "number")
                                return a - b;
                            return String(a).localeCompare(String(b));
                        }),
                    };
                }

                return acc;
            },
            {} as Record<string, { title: string; options: FilterValue[] }>,
        );
    }, [initialFacets, filteredProducts]);

    return { filteredProducts, availableFilters };
};
